import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../../../lib/format";
import {
  parseImportFile, templateCsv, templateXlsxBlob, COLUMN_HELP, TEMPLATE_ROW,
  type ImportFormat, type ParsedRow,
} from "../lib/importParse";

type ImportResult = {
  batch_id: number; new_clients: number; new_estimates: number;
  updated_estimates: number; row_count: number;
  flagged_estimates: number;          // total flagged for Review
  flagged_missing?: number;           // …because they weren't in this file
  flagged_accepted?: number;          // …because QuickBooks now marks them accepted
};

const FORMAT_LABEL: Record<ImportFormat, string> = {
  simple: "standard table (one invoice per row)",
  qbo: "QuickBooks grouped export (legacy layout)",
};

export default function Import() {
  const { current } = useCurrentCompany();
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [format, setFormat] = useState<ImportFormat | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [filename, setFilename] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [flagMissing, setFlagMissing] = useState(true);
  const [importing, setImporting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const successRef = useRef<HTMLDivElement | null>(null);

  // Elapsed-seconds counter shown next to the spinner so the user knows it's alive.
  useEffect(() => {
    if (!importing) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [importing]);

  // Auto-scroll to the success banner once we get a result.
  useEffect(() => {
    if (result && successRef.current) {
      successRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  async function handleFile(f: File) {
    setError(null); setResult(null); setWarnings([]); setFormat(null); setRows([]);
    setFilename(f.name);
    try {
      const parsed = await parseImportFile(f);
      setFormat(parsed.format);
      setWarnings(parsed.warnings);
      setRows(parsed.rows);
      if (!parsed.rows.length) setError("The file was read, but it contained no invoice rows.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to read file");
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }

  function download(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Hand the user a ready-to-fill sample in the expected layout: header + one demo row.
  function downloadSampleCsv() {
    // Leading BOM so Excel opens the UTF-8 file with Thai client names intact.
    download(new Blob(["﻿" + templateCsv()], { type: "text/csv;charset=utf-8" }), "invoice-import-sample.csv");
  }

  function downloadSampleXlsx() {
    download(templateXlsxBlob(), "invoice-import-sample.xlsx");
  }

  async function doImport() {
    if (!current || !rows.length) return;
    setImporting(true); setError(null); setResult(null);
    try {
      const r = await api<ImportResult>("/api/collection/import", {
        method: "POST",
        body: JSON.stringify({
          company_id: current.id,
          source_filename: filename,
          rows,
          flag_missing: flagMissing,
        }),
      });
      setResult(r);
      setRows([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  function resetForAnotherImport() {
    setResult(null); setError(null); setFilename(null); setRows([]);
    setFormat(null); setWarnings([]);
  }

  const total = rows.reduce((s, r) => s + r.amount, 0);
  const uniqueCustomers = new Set(rows.map((r) => r.customer)).size;
  const currency = current?.currency || "THB";

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="text-base font-semibold">Import invoices</div>
        <p className="mt-1 text-sm text-slate-600">
          Drop a spreadsheet with a header row and <strong>one invoice per row</strong>.
          {" "}<strong>.xls</strong>, <strong>.xlsx</strong> and <strong>.csv</strong> all work, the column order
          doesn't matter, and extra columns are ignored. We parse it in your browser — the file itself never leaves
          the app.
        </p>

        <label
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`mt-4 flex h-32 cursor-pointer items-center justify-center rounded-md border-2 border-dashed px-4 text-center transition ${
            dragging ? "border-indigo-400 bg-indigo-50" : "border-slate-300 bg-slate-50 hover:bg-slate-100"
          }`}
        >
          <input
            type="file" accept=".xls,.xlsx,.csv" className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
          <div className="text-sm text-slate-600">
            {filename
              ? <><strong>{filename}</strong> &nbsp;·&nbsp; click to choose another</>
              : "Drop an .xls, .xlsx or .csv here, or click to choose"}
          </div>
        </label>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-500">Sample file with one demo row:</span>
          <button type="button" onClick={downloadSampleXlsx} className="btn-secondary px-2.5 py-1 text-xs">
            Download sample .xlsx
          </button>
          <button type="button" onClick={downloadSampleCsv} className="btn-secondary px-2.5 py-1 text-xs">
            Download sample .csv
          </button>
        </div>

        {error && <div className="mt-3 rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        {warnings.length > 0 && (
          <div className="mt-3 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        )}

        {rows.length > 0 && !result && (
          <div className="mt-4 space-y-3">
            {format && (
              <div className="text-xs text-slate-500">Read as {FORMAT_LABEL[format]}.</div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Rows" value={String(rows.length)} />
              <Stat label="Customers" value={String(uniqueCustomers)} />
              <Stat label="Total" value={fmtMoney(total, currency)} />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={flagMissing} onChange={(e) => setFlagMissing(e.target.checked)} />
              <span>
                Flag invoices that are <strong>not</strong> in this file for review
                <span className="block text-xs text-slate-500">
                  Open invoices and quotations alike. They keep their current status and appear in <strong>Review</strong>,
                  so you can confirm Paid, write it off, or leave it — nothing is closed automatically.
                </span>
              </span>
            </label>
            <button className="btn-primary" onClick={doImport} disabled={importing}>
              {importing
                ? <><Spinner /> Importing {rows.length} rows… {elapsed}s</>
                : <>Confirm import → {current?.name}</>}
            </button>
            {importing && (
              <div className="text-xs text-slate-500">
                Processing each row in the database — for ~300 rows this can take 15–30 seconds.
                Don't close the tab.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reference: the exact file layout and what an import does to existing data.
          Open by default until a file is loaded, so it's there when you need it. */}
      <RulesPanel open={!rows.length && !result} />

      {result && (
        <div ref={successRef} className="card overflow-hidden ring-1 ring-emerald-200">
          <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-white px-5 py-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            </div>
            <div className="flex-1">
              <div className="font-semibold text-slate-900">Import complete</div>
              <div className="text-xs text-slate-500">
                Batch #{result.batch_id} · {filename || "uploaded file"}
              </div>
            </div>
            <button className="btn-secondary" onClick={resetForAnotherImport}>Import another</button>
          </div>
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-5">
            <Stat label="Rows processed" value={String(result.row_count)} />
            <Stat label="New clients" value={String(result.new_clients)} />
            <Stat label="New invoices" value={String(result.new_estimates)} />
            <Stat label="Updated" value={String(result.updated_estimates)} />
            <Stat label="Flagged for review" value={String(result.flagged_estimates)} />
          </div>
          {result.flagged_estimates > 0 && (
            <div className="border-t border-amber-100 bg-amber-50 px-5 py-3 text-sm text-amber-800">
              <strong>{result.flagged_estimates}</strong> open invoice{result.flagged_estimates === 1 ? "" : "s"} flagged for review —
              they stay open and keep being chased until you check them.
              <ul className="mt-1 list-disc pl-5 text-xs">
                {(result.flagged_missing ?? result.flagged_estimates) > 0 && (
                  <li>{result.flagged_missing ?? result.flagged_estimates} not in this file — likely closed in QuickBooks.</li>
                )}
                {(result.flagged_accepted ?? 0) > 0 && (
                  <li>{result.flagged_accepted} newly <strong>accepted</strong> in QuickBooks — billed or about to be, but not confirmed paid.</li>
                )}
              </ul>
              <Link to="/collection/review" className="mt-1 inline-block font-semibold underline">Review now →</Link>
            </div>
          )}
        </div>
      )}

      {rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left uppercase text-slate-500">
              <tr><th className="px-3 py-2">Customer</th><th>Date</th><th>Number</th><th>Status</th>
                  <th className="text-right">Before tax</th><th className="text-right">Tax</th>
                  <th className="text-right">Amount</th><th className="px-3">Memo</th></tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-1">{r.customer}</td>
                  <td>{fmtDate(r.date)}</td>
                  <td className="font-mono">{r.number}</td>
                  <td>{r.status}</td>
                  <td className="text-right font-mono text-slate-500">{fmtMoney(r.taxable || 0, currency)}</td>
                  <td className="text-right font-mono text-slate-500">{fmtMoney(r.tax || 0, currency)}</td>
                  <td className="text-right font-mono">{fmtMoney(r.amount, currency)}</td>
                  <td className="max-w-md truncate px-3 text-slate-500">{r.memo}</td>
                </tr>
              ))}
              {rows.length > 100 && (
                <tr><td colSpan={8} className="px-3 py-2 text-center text-slate-500">
                  …and {rows.length - 100} more rows
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Everything you need to remember about the file and the import, so you don't have to.
function RulesPanel({ open }: { open: boolean }) {
  return (
    <details open={open} className="card p-5 [&_summary::-webkit-details-marker]:hidden">
      <summary className="cursor-pointer list-none text-base font-semibold text-slate-900">
        File format &amp; what an import does
        <span className="ml-2 text-xs font-normal text-slate-500">(click to show / hide)</span>
      </summary>

      <div className="mt-4 space-y-6">
        <section>
          <h3 className="text-sm font-semibold text-slate-900">The columns</h3>
          <p className="mt-1 text-xs text-slate-500">
            One header row, then one invoice per row. Order doesn't matter, extra columns are ignored, and the
            header spellings below are all recognised.
          </p>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Column</th>
                  <th className="px-3 py-2">Demo value</th>
                  <th className="px-3 py-2">Also accepted</th>
                  <th className="px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {COLUMN_HELP.map((c) => (
                  <tr key={c.header} className="border-t border-slate-100 align-top">
                    <td className="whitespace-nowrap px-3 py-1.5 font-semibold text-slate-800">
                      {c.header}
                      {c.required
                        ? <span className="ml-1.5 rounded bg-rose-50 px-1 text-[10px] font-semibold text-rose-700">required</span>
                        : <span className="ml-1.5 rounded bg-slate-100 px-1 text-[10px] font-medium text-slate-500">optional</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-slate-600">{c.example}</td>
                    <td className="px-3 py-1.5 text-slate-500">{c.alsoAccepted}</td>
                    <td className="px-3 py-1.5 text-slate-500">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            The sample files above contain exactly this header row plus this one demo row:{" "}
            <span className="font-mono text-slate-600">{TEMPLATE_ROW.join(" · ")}</span>
          </p>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-slate-900">What an import does to data already in the system</h3>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            <RuleCard
              tone="emerald"
              title="Nothing is ever deleted"
              points={[
                "No client and no invoice is removed by an import.",
                "No invoice is marked Paid or closed automatically.",
                "Safe to re-run: importing the same file twice changes nothing the second time.",
              ]}
            />
            <RuleCard
              tone="amber"
              title="Overwritten on invoices already here"
              points={[
                "An invoice is matched by client + Number. If that pair already exists, the file wins on: Date, Amount, Before tax, Tax, Status and Memo.",
                "A blank Memo cell clears a memo you typed in the app.",
                "A client name we haven't seen creates a new client (English, 30-day terms) — fill in their email afterwards.",
              ]}
            />
            <RuleCard
              tone="slate"
              title="Never touched by an import"
              points={[
                "Paid / Written off / Quotation-only stays as you set it — a paid invoice is not reopened just because it's still in the file.",
                "Manual SMS overrides.",
                "Client details: email, contact, language, credit terms and rate are only set when the client is first created.",
              ]}
            />
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-slate-900">What gets flagged for Review</h3>
          <p className="mt-1 text-xs text-slate-600">
            Flagged invoices stay <strong>open</strong> and keep being chased. Nothing is ever closed
            automatically — the flag just puts them in front of you.
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-slate-600">
            <li>
              <strong>Not in this file</strong> — with the checkbox above ticked, any live invoice absent from the
              file is flagged; it has likely been closed in QuickBooks. That covers quotations as well as open
              invoices: a quotation that leaves QuickBooks has no other way to surface, so it would otherwise sit
              in the book unnoticed. Untick the box to leave them alone. Re-importing one clears its flag
              automatically.
            </li>
            <li>
              <strong>Status turned "accepted"</strong> — the customer took the quote, so it's billed or about to
              be, but that is not payment. Flagged whatever the checkbox says. Only the change to accepted raises
              it, so dismissing it in Review sticks: later imports won't raise it again while it stays accepted.
              A brand-new invoice that arrives already accepted is flagged on first sight.
            </li>
            <li>If the file reads as zero rows, nothing is flagged — a bad file can't flag your whole book.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-slate-900">Rows we skip</h3>
          <p className="mt-1 text-xs text-slate-600">
            A row with no Customer, no Number, an unreadable Date or an unreadable Amount is skipped — you'll see a
            count of them before you confirm. Blank spacer rows and a trailing <em>Total</em> row are ignored
            silently. Nothing is imported until you press <strong>Confirm import</strong>.
          </p>
        </section>
      </div>
    </details>
  );
}

const RULE_TONES = {
  emerald: "bg-emerald-50 ring-emerald-200 text-emerald-900",
  amber: "bg-amber-50 ring-amber-200 text-amber-900",
  slate: "bg-slate-50 ring-slate-200 text-slate-800",
} as const;

function RuleCard({ tone, title, points }: {
  tone: keyof typeof RULE_TONES; title: string; points: string[];
}) {
  return (
    <div className={`rounded-md p-3 ring-1 ${RULE_TONES[tone]}`}>
      <div className="text-xs font-semibold">{title}</div>
      <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs opacity-90">
        {points.map((p, i) => <li key={i}>{p}</li>)}
      </ul>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold tracking-tightish text-slate-900">{value}</div>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin text-current" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
